import React, { useState, useEffect, useReducer, useContext } from 'react'
import styled from 'styled-components'
import { Button, Card, Container, Text, Tooltip } from 'pcln-design-system'
import { Popover } from '@/components/Popover'
import ClickOutsideWrapper from '@/components/ClickOutsideWrapper'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import Menu from './Menu'
import MenuItemsPanel from './MenuItemsPanel'
import ChildAgeSelectors from './ChildAgeSelectors'
import renderMenuLabel from './renderMenuLabel'
import { TRAVELER_SELECTION_CONTAINER_MAX_WIDTH } from './constants'
import { Travelers, TravelerKey, TravelerSelectionType } from './types'

const errorTooltipBaseStyles = {
  bottom: true,
  color: 'error',
  right: true,
  zIndex: 1
}

const MenuCard = styled(Card)`
  user-select: none;
  z-index: 4;
  border-color: transparent;
`

const StyledButton = styled(Button)`
  display: block;
  margin: 0 auto 16px auto;
  width: 85%;
`

const StyledPopover = styled(Popover)`
  padding: ${props => (props.isMobile && props.open ? '16px' : 0)};
  top: ${props => (props.isMobile && props.open ? '8px' : 0)};
  pointer-events: ${props => (props.disabled ? 'none' : undefined)};
`

function travelerReducer(
  state: Travelers,
  action: {
    type: string
    ages?: number[]
    travelerKey?: TravelerKey
  }
) {
  const { ages = [], travelerKey } = action

  switch (action.type) {
    case 'UPDATE_AGES': {
      return { ...state, ages }
    }
    case 'ADD_TRAVELER': {
      return {
        ...state,
        [travelerKey as TravelerKey]: state[travelerKey as TravelerKey] + 1
      }
    }
    case 'REMOVE_TRAVELER': {
      const newAges =
        travelerKey !== 'adultCount'
          ? state.ages.slice(0, state.ages.length - 1)
          : state.ages

      return {
        ...state,
        ages: newAges,
        [travelerKey as TravelerKey]: state[travelerKey as TravelerKey] - 1
      }
    }
    default:
      return state
  }
}

export default function TravelerSelection({
  adultCount = 0,
  childrenAges = [],
  childrenCount = 0,
  disableShadowState,
  disclaimer,
  error,
  hotelPlannerLinkAttrs,
  infantCount = 0,
  isMobile = false,
  maxAdults,
  maxChildren,
  maxRooms,
  onDoneButtonClick,
  roomCount = 0,
  setValue,
  showChildAgeSelectors = false,
  showInfantMenuItem = false,
  showRoomMenuItem = false,
  showSubLabel = false,
  showYouthMenuItem = false,
  youthCount = 0,
  disabled = false,
  childAgeSelectors
}: TravelerSelectionType) {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [travelers, setTravelers] = useReducer(travelerReducer, {
    adultCount,
    childrenCount,
    infantCount,
    roomCount,
    youthCount,
    ages: childrenAges
  })
  const { isIOS } = useContext(BootstrapDataContext)
  useEffect(() => {
    setValue('adultCount', travelers.adultCount, { shouldDirty: true })
  }, [travelers.adultCount, setValue])

  useEffect(() => {
    setValue('childrenAges', travelers.ages, { shouldDirty: true })
  }, [travelers.ages, setValue])

  useEffect(() => {
    setValue('childrenCount', travelers.childrenCount, { shouldDirty: true })
  }, [travelers.childrenCount, setValue])

  useEffect(() => {
    setValue('infantCount', travelers.infantCount, { shouldDirty: true })
  }, [travelers.infantCount, setValue])

  useEffect(() => {
    setValue('youthCount', travelers.youthCount, { shouldDirty: true })
  }, [travelers.youthCount, setValue])

  useEffect(() => {
    setValue('roomCount', travelers.roomCount, { shouldDirty: true })
  }, [travelers.roomCount, setValue])

  const toggleMenu = (open: boolean) => (e: MouseEvent) => {
    e.preventDefault()
    if (open) {
      disableShadowState()
    }
    setIsPanelOpen(!open)
  }

  return (
    <ClickOutsideWrapper setIsPanelOpen={() => disableShadowState()}>
      <Container
        data-testid="traveler-selection"
        maxWidth={TRAVELER_SELECTION_CONTAINER_MAX_WIDTH}
      >
        <StyledPopover
          disabled={disabled}
          color="background.lightest"
          data-traveler-selection="true"
          key="traveler-selection"
          borderRadius={['2xl', null, 'lg']}
          onDismiss={() => {
            setIsPanelOpen(!isPanelOpen)
          }}
          open={isPanelOpen}
          title="Traveler Selection"
          width={1}
          zIndex={isPanelOpen ? 2 : 0}
          isMobile={isMobile}
        >
          <Menu
            disabled={disabled}
            hasError={error.length > 0}
            isMobile={isMobile}
            isPanelOpen={isPanelOpen}
            label={renderMenuLabel({
              ...travelers,
              showInfantMenuItem,
              showRoomMenuItem,
              showYouthMenuItem
            })}
            toggleMenu={toggleMenu}
          />
          {!isPanelOpen && error && (
            <Tooltip {...errorTooltipBaseStyles}>{error}</Tooltip>
          )}
          {isPanelOpen && (
            <Popover.Body
              color={['background.light', null, 'transparent']}
              mt={[0, null, 2]}
              mr={[0, null, -3, 0]}
              openState={isPanelOpen}
            >
              <MenuCard
                mt={isIOS ? 2 : 0}
                boxShadowSize={['none', null, 'overlay-lg']}
                color="background.lightest"
                data-testid="traveler-selection-control-panel"
                id="traveler-selection-control-panel"
                title="Traveler Selection Menu"
                width={[1, null, null, null, 280]}
                isMobile={isMobile}
                borderRadius={['lg', null, 'xl']}
              >
                <MenuItemsPanel
                  hotelPlannerLinkAttrs={hotelPlannerLinkAttrs}
                  maxAdults={maxAdults}
                  maxChildren={maxChildren}
                  maxRooms={maxRooms}
                  setTravelers={setTravelers}
                  showInfantMenuItem={showInfantMenuItem}
                  showRoomMenuItem={showRoomMenuItem}
                  showSubLabel={showSubLabel}
                  showYouthMenuItem={showYouthMenuItem}
                  travelers={travelers}
                />
                {showChildAgeSelectors && (
                  <ChildAgeSelectors
                    ages={travelers.ages}
                    childrenCount={childrenCount}
                    setTravelers={setTravelers}
                    childAgeSelectors={childAgeSelectors}
                  />
                )}
                <StyledButton
                  id="traveler-selection-done-button"
                  data-testid="traveler-selection-done-button"
                  onClick={() => {
                    setIsPanelOpen(false)
                    onDoneButtonClick()
                  }}
                  title="traveler-selection-done-button"
                  type="button"
                >
                  Done
                </StyledButton>
                {disclaimer && (
                  <Text fontSize={0} m={3}>
                    {disclaimer}
                  </Text>
                )}
              </MenuCard>
            </Popover.Body>
          )}
        </StyledPopover>
      </Container>
    </ClickOutsideWrapper>
  )
}
